import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { URL_MAPPING, CORE_SERVICES, MAIN_AREAS } from '../data/siteData';

export default function SitemapPage() {
  const [copied, setCopied] = useState(false);

  const xmlSitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${URL_MAPPING.map(
  (item) => `  <url>
    <loc>https://xn--hrvejenstrpleje-xlbj.dk${item.newUrl === '/' ? '' : item.newUrl}</loc>
    <changefreq>weekly</changefreq>
    <priority>${item.newUrl === '/' ? '1.0' : item.newUrl.startsWith('/traefaeldning-i') || item.newUrl.startsWith('/topkapning-') || item.newUrl.startsWith('/rodfraesning-') ? '0.8' : '0.9'}</priority>
  </url>`
).join('\n')}
</urlset>`;

  const copyXml = () => {
    navigator.clipboard.writeText(xmlSitemap);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <>
      <SEO
        title="Sitemap - Hærvejens Træpleje"
        description="Komplet oversigt over alle publicerede sider, kerneydelser, geografiske områder og lokale landingssider hos Hærvejens Træpleje."
        canonical="https://xn--hrvejenstrpleje-xlbj.dk/sitemap"
      />

      <Breadcrumbs items={[{ label: 'Sitemap' }]} />

      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Struktureret oversigt
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Sitemap over Hærvejens Træpleje
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            Her finder du en komplet fortegnelse over alle publicerede ydelser, byprofiler og lokale SEO-landingssider for nem crawling og navigation.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Hovedsider */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#dce5df] shadow-xs">
            <h2 className="text-lg font-bold text-[#14291d] mb-4 pb-2 border-b border-[#edf3ef] flex items-center gap-2">
              <i className="fa-solid fa-compass text-[#2d6a4f]" aria-hidden="true" />
              <span>Hovedsider</span>
            </h2>
            <ul className="space-y-2 text-sm text-[#384b3f]">
              <li>
                <Link to="/" className="hover:text-[#1b4332] hover:underline font-medium">
                  Forside (/)
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-[#1b4332] hover:underline">
                  Ydelser oversigt (/services)
                </Link>
              </li>
              <li>
                <Link to="/omraader" className="hover:text-[#1b4332] hover:underline">
                  Områder vi dækker - Central Hub (/omraader)
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="hover:text-[#1b4332] hover:underline">
                  Om Hærvejens Træpleje (/om-os)
                </Link>
              </li>
              <li>
                <Link to="/referencer" className="hover:text-[#1b4332] hover:underline">
                  Referencer & Galleri (/referencer)
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-[#1b4332] hover:underline">
                  Kontakt (/kontakt)
                </Link>
              </li>
              <li>
                <Link to="/english" className="hover:text-[#1b4332] hover:underline">
                  English (/english)
                </Link>
              </li>
              <li>
                <Link to="/deutsch" className="hover:text-[#1b4332] hover:underline">
                  Deutsch (/deutsch)
                </Link>
              </li>
            </ul>
          </div>

          {/* Kerneydelser */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#dce5df] shadow-xs">
            <h2 className="text-lg font-bold text-[#14291d] mb-4 pb-2 border-b border-[#edf3ef] flex items-center gap-2">
              <i className="fa-solid fa-tree text-[#2d6a4f]" aria-hidden="true" />
              <span>Kerneydelser</span>
            </h2>
            <ul className="space-y-2 text-sm text-[#384b3f]">
              {CORE_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="hover:text-[#1b4332] hover:underline">
                    {s.title} (/{s.slug})
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Bysider */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#dce5df] shadow-xs">
            <h2 className="text-lg font-bold text-[#14291d] mb-4 pb-2 border-b border-[#edf3ef] flex items-center gap-2">
              <i className="fa-solid fa-location-dot text-[#2d6a4f]" aria-hidden="true" />
              <span>Byprofiler</span>
            </h2>
            <ul className="space-y-2 text-sm text-[#384b3f]">
              {MAIN_AREAS.map((a) => (
                <li key={a.slug}>
                  <Link to={`/${a.slug}`} className="hover:text-[#1b4332] hover:underline">
                    Træpleje i {a.name} (/{a.slug})
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Alle 18 lokale SEO-sider */}
        <div className="bg-white rounded-2xl p-6 sm:p-10 border border-[#dce5df] shadow-xs mb-16">
          <h2 className="text-xl font-bold text-[#14291d] mb-6 pb-3 border-b border-[#edf3ef] flex items-center gap-2">
            <i className="fa-solid fa-sitemap text-[#2d6a4f]" aria-hidden="true" />
            <span>Alle 18 lokale SEO-landingssider (opdelt efter by)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MAIN_AREAS.map((area) => (
              <div key={area.slug} className="p-4 rounded-xl bg-[#faf9f5] border border-[#e5dfd2]">
                <h3 className="font-bold text-sm text-[#14291d] mb-2 flex items-center gap-1.5">
                  <i className="fa-solid fa-city text-xs text-[#2d6a4f]" aria-hidden="true" />
                  <span>{area.name}</span>
                </h3>
                <ul className="space-y-1.5 text-xs text-[#384b3f]">
                  {area.localPages.map((lp) => (
                    <li key={lp.slug}>
                      <Link
                        to={`/${lp.slug}`}
                        className="hover:text-[#1b4332] hover:underline flex items-center justify-between"
                      >
                        <span>{lp.title}</span>
                        <span className="text-[10px] text-[#6d8475] font-mono">/{lp.slug}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* XML Sitemap Feed */}
        <div className="bg-[#12281d] text-[#e0ece5] rounded-2xl p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">XML Sitemap specifikation</h3>
              <p className="text-xs text-[#9bb3a4]">Standard sitemap.xml format for søgemaskiner</p>
            </div>
            <button
              type="button"
              onClick={copyXml}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1f4a36] hover:bg-[#296046] text-white text-xs font-semibold transition-colors cursor-pointer self-start sm:self-auto"
            >
              <i className={copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'} aria-hidden="true" />
              <span>{copied ? 'Kopieret til udklipsholder!' : 'Kopiér XML'}</span>
            </button>
          </div>
          <pre className="bg-[#0b1b13] p-4 rounded-xl text-xs font-mono overflow-x-auto max-h-72 border border-[#1f4734] text-[#a9c9b7]">
            {xmlSitemap}
          </pre>
        </div>
      </section>
    </>
  );
}
